import { fmtDate as fmtDateAU, fmtIfDate } from "@/lib/intl";
import { AlertTriangle, CheckCircle2, HelpCircle, TrendingUp } from "lucide-react";

// Status text + icon always shown together — colour is never the only signal.
export const STATUS = {
  green: { label: "Meeting target", icon: CheckCircle2, chip: "bg-emerald-50 text-emerald-800 border-emerald-300", bar: "bg-emerald-600" },
  amber: { label: "Building", icon: TrendingUp, chip: "bg-amber-50 text-amber-900 border-amber-300", bar: "bg-amber-500" },
  red: { label: "Needs attention", icon: AlertTriangle, chip: "bg-red-50 text-red-800 border-red-300", bar: "bg-red-600" },
  not_assessable: { label: "Not enough information", icon: HelpCircle, chip: "bg-zinc-100 text-zinc-700 border-zinc-300", bar: "bg-zinc-400" },
};

export const PRIORITY = {
  urgent: "bg-red-700 text-white",
  high: "bg-red-100 text-red-900 border border-red-300",
  normal: "bg-sky-100 text-sky-900 border border-sky-300",
  routine: "bg-zinc-100 text-zinc-800 border border-zinc-300",
  low: "bg-zinc-50 text-zinc-600 border border-zinc-200",
};

export const RESOLUTIONS = [
  { value: "contacted_client", label: "Contacted client" },
  { value: "reviewed_no_action", label: "Reviewed — no action needed" },
  { value: "program_adjusted", label: "Program adjusted" },
  { value: "referred", label: "Referred to treating team" },
  { value: "other", label: "Other" },
];

export const FEEDBACK_MODES = {
  hybrid: "Hybrid — release low-risk summaries, hold flagged ones",
  review: "Review before release — hold every summary",
  automatic: "Automatic — release unless a safety flag",
};

const NUMERIC = new Set(["integer", "decimal", "duration", "rating"]);

export function isBlank(q, v) {
  if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) return true;
  return !!q && NUMERIC.has(q.type) && v === "unsure";
}

// Mirror of backend checkins/engine.ev — branching only.
export function evalCond(cond, ans, qs) {
  if (!cond) return true;
  if (cond.all) return cond.all.every((c) => evalCond(c, ans, qs));
  if (cond.any) return cond.any.some((c) => evalCond(c, ans, qs));
  if (cond.not) return !evalCond(cond.not, ans, qs);
  if (cond.always) return true;
  const v = ans[cond.q];
  const q = qs[cond.q];
  if (cond.op === "unanswered") return isBlank(q, v);
  if (cond.op === "answered") return !isBlank(q, v);
  if (isBlank(q, v)) return false;
  const n = typeof v === "number" ? v : null;
  const t = cond.value;
  switch (cond.op) {
    case "gt": return n !== null && n > t;
    case "gte": return n !== null && n >= t;
    case "lt": return n !== null && n < t;
    case "lte": return n !== null && n <= t;
    case "eq": return v === t;
    case "ne": return v !== t;
    case "in": return t.includes(v);
    case "any_of": return (Array.isArray(v) ? v : [v]).some((x) => t.includes(x));
    case "count_gte": return Array.isArray(v) && v.length >= t;
    default: return false;
  }
}

export function visibleQuestions(template, answers) {
  const qs = Object.fromEntries(template.questions.map((q) => [q.code, q]));
  const eff = {};
  const out = [];
  for (const q of template.questions) {
    if (q.show_if && !evalCond(q.show_if, eff, qs)) continue;
    eff[q.code] = answers[q.code];
    out.push(q);
  }
  return out;
}

export function displayAnswer(q, v) {
  if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) return "Not answered";
  if (v === "unsure") return "Not sure";
  const labels = Object.fromEntries((q.options || []).map((o) => [o.value, o.label]));
  if (Array.isArray(v)) return v.map((x) => labels[x] || x).join(", ");
  if (labels[v]) return labels[v];
  return q.unit ? `${v} ${q.unit}` : String(fmtIfDate(v));
}

export function checkAnswer(q, v) {
  if (isBlank(null, v)) return q.required ? "Please answer this question." : null;
  if (v === "unsure") return null;
  if (NUMERIC.has(q.type)) {
    if (typeof v !== "number" || Number.isNaN(v)) return "Please enter a number.";
    if (q.type !== "decimal" && !Number.isInteger(v)) return "Please enter a whole number.";
    if ((q.min != null && v < q.min) || (q.max != null && v > q.max)) return `Please enter a value between ${q.min} and ${q.max}.`;
  }
  return null;
}

export const fmtDate = (iso) => (iso ? fmtDateAU(iso) : "—");
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—");
